import React from 'react'
import { useAtom } from 'jotai'
import { filesAtom } from '@store/atoms'
import { UploadBox } from '../../components/UploadBox'
import {
	currentSideAtom,
	documentModeAtom,
	documentProgressAtom,
	documentSubStepIndexAtom,
	type Side,
} from '@ui/richiesta/stepper/data/store'
import { useUpload } from '../../components/modal-views/hooks/use-upload.hook'
import type { UserDocument } from '@hooks/use-ocr.hooks.ts'
import Loader from '@ui/components/loader'
import { OcrErrorModal } from './OcrErrorModal'
import { isDeviceMobile } from '@utils/mobile.ts'

type DocumentUploadProps = {
	subStepId: number
	docId: UserDocument
	maxDocs?: number
	minDocs?: number

	// NEW: il parent registra una function che farà partire uploadFiles()
	registerSubmit?: (fn: () => void) => void
	registerSave?: (fn: () => void) => void
	onSaveSuccess: (id: number) => void
	onRetryOcr: () => void
}

export default function DocumentUpload({
	subStepId,
	docId,
	maxDocs = 2,
	minDocs = 2,
	registerSubmit,
	registerSave,
	onSaveSuccess,
	onRetryOcr,
}: DocumentUploadProps) {
	const {
		files: uploadFilesList,
		updateFiles,
		removeFile: removeFromUploadList,
		uploadFiles,
		showLoader,
		hasError,
		canGoFurther,
		saveDocuments,
		uploadedFiles,
	} = useUpload(docId, maxDocs, minDocs)

	const [mode, setMode] = useAtom(documentModeAtom)
	const [currentSide, setCurrentSide] = useAtom(currentSideAtom)
	const [filesMap, setFilesMap] = useAtom(filesAtom)

	const substepFiles = filesMap[subStepId] ?? { front: null, back: null }
	const currentFile = substepFiles[currentSide]
	const [subStepIndex, setSubStepIndex] = useAtom(documentSubStepIndexAtom) //counter relativi a documentFlow

	const [documentProgress, setDocumentProgress] = useAtom(documentProgressAtom)

	const errorDesc = documentProgress?.[subStepId]?.error ?? null
	const errorTitle = documentProgress?.[subStepId]?.errorTitle ?? null

	const hasOcrError = !!errorDesc
	const isSubmitting = documentProgress?.[subStepId]?.submitting === true

	const [isModalOpen, setIsModalOpen] = React.useState(false)
	const closeModal = () => setIsModalOpen(false)

	const retryOcr = React.useCallback(() => {
		// chiudi modale (togli errore)
		setDocumentProgress((prev) => ({
			...prev,
			[subStepId]: {
				...(prev[subStepId] ?? { stepIndex: 0, completed: false }),
				error: null,
				errorTitle: null,
			},
		}))

		// consenti nuovo “success notify” se serve
		notifiedSaveRef.current = false

		// chiama OCR vero (parent)
		onRetryOcr()
	}, [onRetryOcr, setDocumentProgress, subStepId])

	React.useEffect(() => {
		if (!registerSave) return

		registerSave(() => {
			saveDocuments()
		})
	}, [registerSave])

	const notifiedSaveRef = React.useRef(false)

	React.useEffect(() => {
		if (!onSaveSuccess) return
		if (!uploadedFiles) return

		const ok = hasError.type === 'NONE' && uploadedFiles?.idRichiesta != null

		if (!ok) {
			// se vuoi permettere un nuovo tentativo dopo un reset
			notifiedSaveRef.current = false
			return
		}

		if (notifiedSaveRef.current) return

		notifiedSaveRef.current = true

		onSaveSuccess(subStepId) //  lanci markCompletedAndExit(subStepId) dal parent
	}, [uploadedFiles, hasError.type, onSaveSuccess, subStepId])

	//ENTRAMBE CARICATI
	const hasBothFiles = !!substepFiles?.front && !!substepFiles?.back

	const hasFront = !!substepFiles?.front
	const hasBack = !!substepFiles?.back

	const clearBothAndRetry = () => {
		const fFront = substepFiles.front
		const fBack = substepFiles.back

		if (fFront) removeFromUploadList(fFront.name)
		if (fBack) removeFromUploadList(fBack.name)

		setFilesMap((prev) => ({
			...prev,
			[subStepId]: {
				...(prev[subStepId] ?? {}),
				front: null,
				back: null,
			},
		}))

		setDocumentProgress((prev) => ({
			...prev,
			[subStepId]: {
				...(prev[subStepId] ?? { stepIndex: 0, completed: false }),
				stepIndex: 0, //  reset stepIndex “persistente”
				completed: false,
				submitting: false,
				ocrOk: undefined,
				error: null,
				errorTitle: null,
			},
		}))

		setSubStepIndex(0) //  reset cursore mini-wizard (fondamentale)
		setCurrentSide('front')
		setMode('insert')
	}

	// NEW: espone al parent la submit (invio) con guardie
	React.useEffect(() => {
		if (!registerSubmit) return

		registerSubmit(() => {
			// invia solo se pronto
			if (showLoader) return
			if (!canGoFurther) return
			if (hasError.type !== 'NONE') return

			uploadFiles()
		})
	}, [registerSubmit, uploadFiles, showLoader, canGoFurther, hasError.type])

	const handleSelectFile = (file: File | undefined, side: Side) => {
		if (!file) return

		// evita collisioni nomi front/back
		const ext = file.name.split('.').pop() ?? 'jpg'
		const uniqueId = Math.random().toString(36).slice(2, 8)

		const renamedFile = new File([file], `${side}-${uniqueId}.${ext}`, {
			type: file.type,
		})
		const prevSideFile = substepFiles[side]
		if (prevSideFile) {
			removeFromUploadList(prevSideFile.name)
		}

		setFilesMap((prev) => ({
			...prev,
			[subStepId]: {
				...prev[subStepId],
				[side]: renamedFile,
			},
		}))

		// passa File singolo (updateFiles ora lo supporta)
		updateFiles(renamedFile)

		setCurrentSide(side)
		setMode('preview')
	}

	const removeSideFile = (side: Side) => {
		const f = substepFiles[side]
		if (f) removeFromUploadList(f.name)

		setFilesMap((prev) => ({
			...prev,
			[subStepId]: {
				...prev[subStepId],
				[side]: null,
			},
		}))

		setCurrentSide(side)
		setMode('insert')

		// When a file is removed from the recap view (subStepIndex === 2),
		// reset to the correct upload step so that pressing "Prosegui" from
		// the subsequent preview does not incorrectly trigger the OCR call.
		if (subStepIndex === 2) {
			const resetIndex = side === 'front' ? 0 : 1
			setSubStepIndex(resetIndex)
			setDocumentProgress((prev) => ({
				...prev,
				[subStepId]: {
					...(prev[subStepId] ?? { stepIndex: resetIndex, completed: false }),
					stepIndex: resetIndex,
				},
			}))
		}
	}

	const actionLabel = isDeviceMobile() ? 'Fotografa o allega' : 'Allega'

	let title = ''

	if (hasBothFiles) {
		title = 'Le immagini del tuo documento sono pronte!'
	} else if (hasFront) {
		title = `${actionLabel} il retro del documento`
	} else if (hasBack) {
		title = `${actionLabel} il fronte del documento`
	} else {
		title = `${actionLabel} il fronte e il retro del documento`
	}

	//  PREVIEW
	if (mode === 'preview' && currentFile) {
		const url = URL.createObjectURL(currentFile)

		return (
			<div className='flex flex-col items-center justify-center min-h-[215px] gap-4 mb-44 mt-4'>
				<h2 className='self-start text-(--primary-blue) text-[28px] md:text-[40px] font-semibold'>
					Anteprima del documento
				</h2>
				<p className='self-start font-medium'>
					Se la foto ti soddisfa prosegui, altrimenti riprova
				</p>

				<h3 className='font-bold'>
					{currentSide === 'front' ? 'Fronte' : 'Retro'}
				</h3>

				<div className='w-full flex justify-center items-center border-2 border-dashed border-(--green-default) rounded-xl md:min-h-[215px]'>
					{currentFile.type === 'application/pdf' ? (
						isDeviceMobile() ? (
							<div className='w-full flex flex-col items-center justify-center gap-4 p-4'>
								<div className='w-full text-center text-sm break-all'>
									{currentFile.name}
								</div>

								<a
									href={url}
									target='_blank'
									rel='noopener noreferrer'
									className='
					w-full
					max-w-[320px]
					min-h-10
					px-8
					py-4
					flex
					items-center
					justify-center
					rounded-full
					bg-(--green-default)
					text-white
					text-[18px]
					font-semibold
					text-center
				'
								>
									Apri il documento
								</a>
							</div>
						) : (
							<iframe
								src={url}
								className='h-[215px] w-full max-w-[600px]'
								title='PDF preview'
							/>
						)
					) : (
						<img
							src={url}
							alt='Anteprima documento'
							className='h-[215px] max-w-full object-contain rounded-xl'
						/>
					)}
					{/* {currentFile.type === 'application/pdf' ? (
						<iframe
							src={url}
							className='h-[215px] w-[600px]'
							title='PDF preview'
						/>
					) : (
						<img src={url} alt='preview' className='h-[215px] rounded-xl' />
					)} */}
				</div>

				<div className='flex gap-4'>
					<button
						onClick={() => removeSideFile(currentSide)}
						className='underline text-(--green-default) font-bold cursor-pointer'
					>
						Riprova
					</button>
				</div>

				{/* NB: qui NON inviamo. L’invio lo fa il parent chiamando submit() */}
			</div>
		)
	}

	//  INSERT
	return (
		<div className='flex flex-col gap-4'>
			<Loader isOpen={isSubmitting} />

			<OcrErrorModal
				isOpen={hasOcrError}
				description={errorDesc}
				title={errorTitle}
				onPrimary={clearBothAndRetry}
				onSecondary={retryOcr}
			/>

			<h2 className='text-(--primary-blue) text-[28px] md:text-[40px] font-semibold'>
				{title}
			</h2>
			<p className='text-[13px] md:text-[16px]'>
				{hasBothFiles ? (
					<>
						Controlla che fronte e retro siano corretti.
						<br className='md:hidden' /> Se necessario, puoi sostituirli prima
						di procedere.
					</>
				) : (
					'Puoi allegare file in formato JPG, PNG, TIFF, HEIC/HEIF o PDF di dimensione non superiori a 5MB. I documenti devono essere chiaramente leggibili e non devono contenere riflessi.'
				)}
			</p>

			{hasError?.type !== 'NONE' && (
				<div className='mt-3 p-3 rounded-md border border-(--text-red-alert)'>
					<div className='font-semibold'>{hasError.title}</div>
					<div>{hasError.description}</div>
				</div>
			)}

			<div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-44'>
				<UploadBox
					title='Fronte'
					file={substepFiles.front}
					onFile={(file) => handleSelectFile(file, 'front')}
					onRemove={() => removeSideFile('front')}
					mode={isDeviceMobile() ? 'camera' : 'upload'}
				/>
				<UploadBox
					title='Retro'
					file={substepFiles.back}
					onFile={(file) => handleSelectFile(file, 'back')}
					onRemove={() => removeSideFile('back')}
					mode={isDeviceMobile() ? 'camera' : 'upload'}
				/>
			</div>
		</div>
	)
}
